import { client } from '../session';

// ---------- statistics (A4): the hub proxies `/stats/…` to the datacollector's admin API (docs/contracts.md) ----------
// Organization = PMS customer id (`customerId`); installation = (customerId, workserver). Times UTC ISO, days yyyy-MM-dd.

export interface StatsConfig {
  configured: boolean;
  collectorUrl: string | null;
  /** The AuthTargets entry that is the hub's (and the collector's) AUTH: aliases `pms:` / `workserver:` live there. */
  authTarget: string | null;
}

export interface PageOf<T> {
  items: T[];
  offset: number;
  limit: number;
  hasMore: boolean;
}

export type OrgStatus = 'ok' | 'partial' | 'silent' | 'never' | 'inactive' | 'ignored';
/**
 * `inactive` = marked by an operator, ends by itself when the WorkServer sends data again; `ignored` = not monitored
 * (test, acceptance) until an operator clears it.
 */
export type InstallationState = 'ok' | 'silent' | 'never' | 'inactive' | 'ignored';

export interface Organization {
  customerId: number;
  name: string | null;
  active: boolean;
  location: string | null;
  hasToken: boolean;
  /** Ingest credential (S1); absent from collectors before it. */
  authMode?: AuthMode;
  installations: number;
  silentInstallations: number;
  lastReceived: { logs: string | null; performance: string | null; dailyMetrics: string | null };
  status: OrgStatus;
}

export interface LogFilter {
  line: number;
  regex: string;
  include: boolean;
  severity: number | null;
}

export interface TrackedMethod {
  method: string;
  active: boolean;
  incidentCheck: boolean;
  incidentCheckWorkservers?: string[] | null;
}

export interface Installation {
  customerId: number;
  name: string | null;
  workserver: string;
  version: string | null;
  firstSeen: string | null;
  lastSeen: {
    performance: string | null;
    sys: string | null;
    log: string | null;
    metric: string | null;
    diagnostics: string | null;
  };
  status: InstallationState;
  silentKinds: string[];
  /** The operator's mark while it holds; absent from collectors before it. */
  inactive?: { since: string; by: string; note: string | null } | null;
  /** Not monitored, while set; absent from collectors before it. */
  ignored?: { since: string; by: string; note: string | null } | null;
  /** No timings beyond the heartbeat, but only in hours it usually has none (not silent); absent before 2026.10.6. */
  idle?: boolean;
  /** Hours of the week it usually reports timings (0 = no profile yet: the fixed 2-hour rule). */
  usualHours?: number;
}

/** Which ingest credential the collector accepts for an organization (S1, `NyceLogicAuth:Mode=Optional`). */
export type AuthMode = 'legacy' | 'both' | 'auth';
export const AUTH_MODES: AuthMode[] = ['legacy', 'both', 'auth'];

export interface OrganizationDetail extends Omit<
  Organization,
  'installations' | 'silentInstallations' | 'hasToken'
> {
  alarmSkipWorkserver: string | null;
  filters: LogFilter[];
  performance: TrackedMethod[];
  auth?: { mode: AuthMode; changedAt: string | null; changedBy: string | null };
  token: { issued: boolean; issuedAt: string | null; issuedBy: string | null };
  installations: Installation[];
}

export interface MonitoringUpdate {
  name?: string | null;
  active?: boolean | null;
  location?: string | null;
  alarmSkipWorkserver?: string | null;
  filters?: LogFilter[] | null;
  performance?: TrackedMethod[] | null;
}

export interface IssuedToken {
  customerId: number;
  token: string;
  issuedAt: string;
  note: string;
}

export interface FleetSummary {
  organizationsActive: number;
  organizationsReporting: number;
  installations: { ok: number; silent: number; never: number; inactive?: number; ignored?: number };
  incidentsOpen: number;
  incidentsActive: number;
  errorsOpen: number;
  errorGroupsNew24h: number;
  backlogRows: number;
  rejected24h: number;
  versions: { version: string; installations: number }[];
}

export interface VersionAdoption {
  version: string;
  release: string | null;
  installations: number;
  organizations: number;
  items: { customerId: number; name: string | null; workserver: string; lastSeen: string }[];
}

export interface IngestHealth {
  processor: { status: Record<string, unknown> | null; updatedAt: string | null; stale: boolean };
  backlog: { rows: number; oldestReceivedAt: string | null; lagSeconds: number | null };
  failed: { rows: number; last24h: number; lastFailedAt: string | null };
  jobs: {
    job: string;
    period: string;
    startedAt: string;
    finishedAt: string | null;
    status: string;
    detail: string | null;
  }[];
  rejected24h: { missingToken: number; invalidToken: number; malformed: number };
  rejected24hByEndpoint: { endpoint: string; reason: string; count: number }[];
}

export interface PerfPoint {
  t: string;
  count: number;
  avg: number | null;
  median: number | null;
  p95: number | null;
  p99: number | null;
  min: number | null;
  max: number | null;
}
export interface PerfSeries {
  method: string;
  workserver: string;
  points: PerfPoint[];
}
/** One raw measurement (time to the second; `count` > 1 = a batch operation). */
export interface RawPoint {
  t: string;
  duration: number;
  count: number;
}
/** The raw measurements of a method; `truncated` = more than the collector returns (it keeps the slowest). */
export interface RawSeries {
  method: string;
  workserver: string;
  points: RawPoint[];
  truncated: boolean;
}
export interface TopMethod {
  method: string;
  count: number;
  avg: number | null;
  p95: number | null;
  max: number | null;
  totalMs: number;
}
export interface Regression {
  customerId: number;
  name: string | null;
  workserver: string;
  method: string;
  hour: string;
  count: number;
  currentP95: number;
  baselineP95: number;
  ratio: number;
  baselineSamples: number;
  regressedHours: number;
  evaluatedHours: number;
  versionChange: { from: string | null; to: string; since: string } | null;
}
export interface SystemStat {
  stat: string;
  samples: number;
}
export interface SystemSeries {
  stat: string;
  points: { t: string; avg: number | null; min: number | null; max: number | null; samples: number }[];
}
export interface Volume {
  date: string;
  type: string;
  code: string;
  count: number;
}
export interface Licence {
  date: string;
  type: string;
  count: number;
}

export type EntryStatus = 'open' | 'acknowledged' | 'closed';
export type StatusFilter = 'open' | 'acknowledged' | 'active' | 'closed' | 'all';

export interface ErrorEntry {
  id: string;
  status: EntryStatus;
  customerId: number | null;
  name: string | null;
  workserver: string | null;
  severity: number | null;
  time: string | null;
  message: string | null;
  userId: string | null;
  machine: string | null;
  fingerprint: string | null;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  closedAt: string | null;
  closedBy: string | null;
}
export interface ErrorDetail {
  entry: ErrorEntry;
  source: string | null;
  type: string | null;
  data: unknown;
}
export interface ErrorGroup {
  fingerprint: string;
  title: string;
  sample: string | null;
  count: number;
  open: number;
  acknowledged: number;
  organizations: number;
  installations: { customerId: number; name: string | null; workserver: string | null; count: number }[];
  firstSeen: string | null;
  lastSeen: string;
  new: boolean;
  trend: { date: string; count: number }[];
}

export interface Incident {
  id: string;
  kind: string;
  checkType: string | null;
  customerId: number | null;
  name: string | null;
  workserver: string | null;
  severity: string | null;
  logSeverity: number | null;
  startedAt: string | null;
  endedAt: string | null;
  methods: string[];
  summary: string | null;
  outlierCount: number | null;
  status: EntryStatus;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  closedAt: string | null;
  closedBy: string | null;
}

export type BulkAction = 'ack' | 'close' | 'ackclose';
export interface BulkResult {
  changed: number;
  ids: string[];
}

export interface Diagnostics {
  id: number;
  customerId: number | null;
  created: string | null;
  workserver: string | null;
  version: string | null;
  manual: boolean;
}
export interface DiagnosticsDetail {
  summary: Diagnostics;
  application: unknown;
  database: unknown;
}

export type Frequency = 'daily' | 'weekly' | 'monthly';
export interface Subscription {
  id: number;
  customerId: number;
  customerName: string | null;
  methods: string[];
  recipients: string[];
  workserver: string;
  frequency: Frequency;
  scheduleTime: string;
  scheduleDay: number;
  scheduleDays: number[];
  timezone: string;
  enabled: boolean;
  lastRun: string | null;
  createdAt: string;
  updatedAt: string;
}
export type SubscriptionInput = Omit<Subscription, 'id' | 'lastRun' | 'createdAt' | 'updatedAt'>;
export interface ReportRequest {
  id: number;
  subscriptionId: number;
  kind: 'preview' | 'send';
  status: 'pending' | 'running' | 'ok' | 'failed';
  detail: string | null;
  requestedBy: string;
  requestedAt: string;
  finishedAt: string | null;
  hasPdf: boolean;
}

type Q = Record<string, string | number | boolean | (string | number)[] | null | undefined>;

/** Query string: arrays repeat the key (`method=a&method=b`); empty values are left out. */
export function qs(params: Q): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === '') continue;
    if (Array.isArray(v)) v.forEach((x) => q.append(k, String(x)));
    else q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

const enc = encodeURIComponent;
const get = <T>(path: string, params: Q = {}, signal?: AbortSignal) =>
  client.get<T>(`/stats/${path}${qs(params)}`, { signal });

export const stats = {
  config: () => client.get<StatsConfig>('/stats-config'),
  fleet: () => get<FleetSummary>('fleet/summary'),
  ingest: () => get<IngestHealth>('ingest/health'),
  versions: (days = 14) => get<VersionAdoption[]>('versions', { days }),
  organizations: (q: { search?: string; active?: boolean; offset?: number; limit?: number } = {}) =>
    get<PageOf<Organization>>('organizations', q),
  organization: (customerId: number) => get<OrganizationDetail>(`organizations/${customerId}`),
  updateMonitoring: (customerId: number, body: MonitoringUpdate) =>
    client.put<OrganizationDetail>(`/stats/organizations/${customerId}/monitoring`, body),
  setAuthMode: (customerId: number, mode: AuthMode) =>
    client.put<OrganizationDetail>(`/stats/organizations/${customerId}/auth-mode`, { mode }),
  issueToken: (customerId: number) => client.post<IssuedToken>(`/stats/organizations/${customerId}/token`),
  installations: (q: { customerId?: number; status?: InstallationState } = {}) =>
    get<Installation[]>('installations', q),
  /** Mark an installation inactive (not silent until it sends again) or active; collector audits it too. */
  setInstallationInactive: (body: {
    customerId: number;
    workserver: string;
    inactive: boolean;
    note?: string;
  }) => client.put<Installation>('/stats/installations/inactive', body),
  /** Exclude an installation from monitoring (test, acceptance) or monitor it again; stays until changed. */
  setInstallationIgnored: (body: {
    customerId: number;
    workserver: string;
    ignored: boolean;
    note?: string;
  }) => client.put<Installation>('/stats/installations/ignored', body),
  series: (q: {
    customerId: number;
    workserver?: string;
    method: string[];
    from?: string;
    to?: string;
    /** 1m / 5m / 15m: from the raw measurements (collector 2026-10-29+, last 90 days). */
    bucket?: '1m' | '5m' | '15m' | '1h' | '1d';
  }) => get<PerfSeries[]>('performance/series', q),
  /** The raw measurements (≤ 2 h, last 90 days; collector 2026-10-30+, older: 404). */
  points: (q: { customerId: number; workserver?: string; method: string[]; from: string; to: string }) =>
    get<RawSeries[]>('performance/points', q),
  top: (q: {
    customerId: number;
    workserver?: string;
    /** UTC days. */
    from?: string;
    to?: string;
    /** An exact window (≤ 31 days), wins over the days in a collector that knows it; an older one ranks the days. */
    fromTime?: string;
    toTime?: string;
    sort?: string;
    limit?: number;
  }) => get<TopMethod[]>('performance/top', q),
  regressions: (q: { customerId?: number; workserver?: string; from?: string; to?: string } = {}) =>
    get<Regression[]>('performance/regressions', q),
  systemStats: (q: { customerId: number; workserver: string; from?: string; to?: string }) =>
    get<SystemStat[]>('system/stats', q),
  systemSeries: (q: {
    customerId: number;
    workserver: string;
    stat: string[];
    from?: string;
    to?: string;
    bucket?: '1h' | '1d';
  }) => get<SystemSeries[]>('system/series', q),
  volumes: (q: { customerId: number; workserver?: string; from?: string; to?: string; type?: string[] }) =>
    get<Volume[]>('volumes', q),
  licences: (q: { customerId: number; from?: string; to?: string }) => get<Licence[]>('licences', q),
  errors: (q: {
    customerId?: number;
    workserver?: string;
    from?: string;
    to?: string;
    text?: string;
    status?: StatusFilter;
    fingerprint?: string;
    offset?: number;
    limit?: number;
  }) => get<PageOf<ErrorEntry>>('errors', q),
  error: (id: string) => get<ErrorDetail>(`errors/${enc(id)}`),
  errorGroups: (q: {
    customerId?: number;
    workserver?: string;
    from?: string;
    to?: string;
    status?: 'active' | 'all';
    sort?: 'last' | 'count';
    offset?: number;
    limit?: number;
  }) => get<PageOf<ErrorGroup>>('errors/groups', q),
  errorBulk: (body: { ids?: string[]; fingerprint?: string; customerId?: number; action: BulkAction }) =>
    client.post<BulkResult>('/stats/errors/bulk', body),
  incidents: (q: {
    status?: StatusFilter;
    customerId?: number;
    from?: string;
    to?: string;
    offset?: number;
    limit?: number;
  }) => get<PageOf<Incident>>('incidents', q),
  incidentBulk: (ids: string[], action: BulkAction) =>
    client.post<BulkResult>('/stats/incidents/bulk', { ids, action }),
  diagnostics: (q: { customerId: number; workserver?: string; offset?: number; limit?: number }) =>
    get<PageOf<Diagnostics>>('diagnostics', q),
  diagnosticsDetail: (id: number) => get<DiagnosticsDetail>(`diagnostics/${id}`),
  /** A snapshot sent by mistake (admin): a daily one takes its volumes along; the installation's version follows. */
  deleteDiagnostics: (id: number) => client.del<void>(`/stats/diagnostics/${id}`),
  subscriptions: (customerId?: number) => get<Subscription[]>('reports/subscriptions', { customerId }),
  createSubscription: (body: SubscriptionInput) =>
    client.post<Subscription>('/stats/reports/subscriptions', body),
  updateSubscription: (id: number, body: SubscriptionInput) =>
    client.put<Subscription>(`/stats/reports/subscriptions/${id}`, body),
  deleteSubscription: (id: number) => client.del<void>(`/stats/reports/subscriptions/${id}`),
  preview: (id: number) => client.post<ReportRequest>(`/stats/reports/subscriptions/${id}/preview`),
  testSend: (id: number, recipients?: string[]) =>
    client.post<ReportRequest>(
      `/stats/reports/subscriptions/${id}/test`,
      recipients?.length ? { recipients } : {}
    ),
  request: (id: number) => get<ReportRequest>(`reports/requests/${id}`),
  /** The rendered preview as a Blob (streamed by the hub). */
  pdf: async (id: number) =>
    (
      await client.requestRaw('GET', `/stats/reports/requests/${id}/pdf`, undefined, {
        accept: 'application/pdf',
      })
    ).blob(),
};
